import {
  DetailedLabResultObservation,
  LabResultFindingsEnum,
  LabResultPresentationTypeEnum,
} from '@pm/graphql';
import { Collapsible, Icon, Text } from '@pm/ui';
import { Trans, useTranslation } from 'react-i18next';
import { ObservationTranslationType } from './constants';
import { LabSummary } from './LabSummary';
import {
  formatReferenceRangeDisplay,
  formatValue,
  getResultStyle,
  resolveFindings,
} from './utils';

type LabObservationProps = {
  observation: DetailedLabResultObservation;
  providerName: string;
  showDivider: boolean;
};

export const LabObservation = ({
  observation,
  providerName,
  showDivider,
}: LabObservationProps) => {
  const { t } = useTranslation('labs');

  const i18nKey = `${providerName}_${observation.providerTestIdentifier}`;

  const observationInfoNode = t(`TestNames.${i18nKey}` as never, {
    returnObjects: true,
    defaultValue: {},
  }) as Partial<ObservationTranslationType>;

  const displayName =
    observationInfoNode.Title || observation.testName || t('UnknownTest');
  const description = observationInfoNode.Description || '';

  const { findings, presentationType } = resolveFindings(observation);
  const { color, iconColor, icon } = getResultStyle(findings, presentationType);
  const isFlagged = findings !== LabResultFindingsEnum.Clear;

  // TODO: provide summary data when we have per test copy
  const summarySteps: string[] = [];

  const summaryInstructions =
    summarySteps.length > 0 ? (
      <Text as="div" color="contentInfo">
        <ul className="list-none space-y-s">
          {summarySteps.map((step, i) => (
            // eslint-disable-next-line react-x/no-array-index-key -- TODO: Wait! Don't change this file without seeing if you can remove this disable. It was added during a bulk version update, but fixing it would leave the park nicer than you found it.
            <li key={i}>{step}</li>
          ))}
        </ul>
      </Text>
    ) : null;

  const triggerContent = (
    <div className="flex flex-col gap-xxs">
      <Text size="headingXS">{displayName}</Text>
      <div className="flex items-center gap-xs">
        {presentationType !== LabResultPresentationTypeEnum.Neutral && (
          <Icon name={icon} size="medium" color={iconColor} />
        )}
        <Text size="bodyL" color={color}>
          {formatValue(observation)}
        </Text>
      </div>
    </div>
  );

  const referenceRangeDisplay = formatReferenceRangeDisplay(observation);
  const referenceRangeContent = referenceRangeDisplay && (
    <Text size="bodyS" color="contentSubdued" className="mt-s">
      {t('ReferenceRange')}: {referenceRangeDisplay}
    </Text>
  );

  const descriptionContent = description && (
    <Trans t={t}>
      <Text size="bodyS" color="contentSubdued" className="mt-s">
        {description}
      </Text>
    </Trans>
  );

  const flaggedContent = isFlagged && !!summaryInstructions && (
    <div className="mt-s">
      <LabSummary
        observations={[{ ...observation, displayName }]}
        showTitle={false}
        instructions={summaryInstructions}
      />
    </div>
  );

  const hasBodyContent = Boolean(
    referenceRangeContent || descriptionContent || flaggedContent,
  );

  const bodyContent = hasBodyContent ? (
    <>
      {referenceRangeContent}
      {descriptionContent}
      {flaggedContent}
    </>
  ) : null;

  return (
    <div key={observation.id}>
      {hasBodyContent ? (
        <Collapsible trigger={triggerContent}>{bodyContent}</Collapsible>
      ) : (
        triggerContent
      )}
      {showDivider && <hr className="border-border-subtle my-s" />}
    </div>
  );
};
